import React, { ReactNode, useEffect } from 'react';
import { useRouter } from 'next/router';
import { NextSeo } from 'next-seo';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import Container from '@components/global/Container';
import Layout from '@components/global/Layout';

import getAllPromoTickerData from '@adapters/prismic/fetching/promos/getAllPromoTickerData';
import { PromoTicker } from '@adapters/prismic/types/promoTicker';

import type { GetStaticPropsContext, GetStaticPropsResult } from 'next';
import { getAllStoresDataForSetMyStore } from '@adapters/prismic/fetching/stores';
import { MyStoreData } from '@adapters/prismic/fetching/stores/types';

import { altLocale } from '@utils/locale';
import { fullyEncode, localeAltPathAbs, localePathAbs } from '@utils/url';
import { MEC_TITLE } from '@utils/constants';
import KlarnaPlacement from '@components/product/ProductBuyZone/Klarna/KlarnaPlacement';
import {
  FEATURE_FLAG_KLARNA,
  FEATURE_FLAG_PROMO_BAR_SERVER_SIDE,
} from '@config/feature-flag';
import { handlePromoDetailsTracking } from '@utils/gtm';
import { getPreviewRef } from '@adapters/prismic/utils/getPreviewRef';

import styles from './klarna.module.scss';
import getPromoBarData from '@adapters/prismic/fetching/promos/getPromoBarData';

interface KlarnaInfoPageProps {
  alternateLocaleUrl?: string;
  canonicalUrl: string;
  langAltUrl: string;
  promoMessages?: PromoTicker[];
  xDefault: string;
  stores: any;
}

interface MECStores {
  stores: MyStoreData[];
}

export async function getStaticPaths() {
  return {
    paths: [{ params: { locale: 'en' } }, { params: { locale: 'fr' } }],
    fallback: false,
  };
}

export async function getStaticProps({
  params,
  previewData,
  preview = false,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<
  KlarnaInfoPageProps | MECStores
> | null> {
  const locale = params?.locale === 'fr' ? 'fr' : 'en';
  if (!locale) {
    throw new Error('No locale specified for page');
  }
  if (!FEATURE_FLAG_KLARNA) {
    return {
      notFound: true,
    };
  }

  const previewRef = getPreviewRef(preview, previewData);

  const [stores, promoMessages, serverSideTranslation, promoBarData] =
    await Promise.allSettled([
      getAllStoresDataForSetMyStore(locale),
      !FEATURE_FLAG_PROMO_BAR_SERVER_SIDE &&
        locale &&
        getAllPromoTickerData(locale),
      serverSideTranslations(locale, ['common', 'auth']),
      FEATURE_FLAG_PROMO_BAR_SERVER_SIDE && locale && getPromoBarData(locale),
    ]).then((results) =>
      results.map((promise) => {
        if (
          promise.status === 'rejected' ||
          typeof promise.value === 'undefined'
        ) {
          console.error('promise rejected or returning undefined', promise);
          return null;
        }
        return promise.value;
      })
    );

  const canonicalUrl = localePathAbs(locale, `/klarna`) ?? '';
  const langAltUrl = localeAltPathAbs(locale, `/klarna`) ?? '';
  const alternateLocaleUrl = fullyEncode(langAltUrl) ?? '';
  const xDefault = locale === 'en' ? canonicalUrl : langAltUrl;

  return {
    props: {
      alternateLocaleUrl,
      stores,
      canonicalUrl,
      langAltUrl,
      xDefault,
      ...serverSideTranslation,
      promoMessages,
      promoBarData,
    },
  };
}

export default function KlarnaInfoPage({
  canonicalUrl,
  langAltUrl,
  xDefault,
}: KlarnaInfoPageProps): ReactNode {
  const { query } = useRouter();
  const locale = (query.locale as string) || 'en';
  const { t } = useTranslation('common');

  const description = t('klarna.description');
  const title = t('klarna.heading');

  useEffect(() => {
    handlePromoDetailsTracking('promoDetails');
  }, []);

  return (
    <>
      <NextSeo
        noindex
        description={description}
        title={`${title} ${MEC_TITLE}`}
        canonical={canonicalUrl}
        languageAlternates={[
          { hrefLang: altLocale(locale), href: langAltUrl },
          { hrefLang: String(locale), href: canonicalUrl },
          { hrefLang: 'x-default', href: xDefault },
        ]}
        openGraph={{
          title,
          description,
          locale,
        }}
      />

      <Container>
        <KlarnaPlacement
          className={styles.klarnaInfoContainer}
          placement="info-page"
          purchaseAmount="0"
          locale={locale}
        />
      </Container>
    </>
  );
}

KlarnaInfoPage.Layout = Layout;
